import dynamic from "next/dynamic";
import { forwardRef } from "react";
import { COLOR_TOKENS } from "~/designSystem/constants/token";

import {
  AvatarIconProps,
  AvatarImageProps,
  AvatarInitialProps,
  AvatarLogoProps,
  AvatarProps,
} from "./interfaces";
import { FallbackStyledAvatar } from "./styled/FallbackAvatarStyled";
import { StyledAvatar } from "./styled/StyledAvatar";
import { StyledIconAvatar } from "./styled/StyledIconAvatar";
import { StyledLogoAvatar } from "./styled/StyledLogoAvatar";
import { isValidAvatarColor } from "./utils/ssr";

const Icon = dynamic(() => import("~/designSystem/components/Icon"), {
  ssr: true,
});

/**
 * Avatar component that renders different types of avatars based on the provided props.
 * Supports image, logo, icon, and initial (fallback) variants.
 *
 * @component
 *
 * @example
 * // Image Avatar
 * <Avatar
 *   type="image"
 *   src="/profile.jpg"
 *   alt="User Profile"
 *   size={32}
 *   color="core-blue"
 *   status="active"
 * />
 *
 * @example
 * // Logo Avatar
 * <Avatar
 *   type="logo"
 *   src="/company-logo.svg"
 *   alt="Company Logo"
 *   size={40}
 *   stroke
 * />
 *
 * @example
 * // Icon Avatar
 * <Avatar
 *   type="icon"
 *   icon={<Icon name="Sparkle2" type="outline" size={12} />}
 *   size={24}
 *   status="disabled"
 * />
 *
 * @example
 * // Initial Avatar (Fallback)
 * <Avatar
 *   type="initial"
 *   alt="John Doe"
 *   color="core-silver"
 *   size={36}
 * />
 *
 * @param props - Component props
 *
 * @param type - Determines the type of avatar to render (image, logo, icon, initial)
 * @param size - Size of the avatar in pixels (default: 24)
 * @param status - Status of the avatar ("active" | "disabled")
 * @param stroke - Whether to show a border stroke around the avatar
 *
 * // Image Avatar specific props
 * @param src - Required for type="image". URL of the image to display
 * @param color - Color token for image avatar border (default: "core-silver") available options: "core-silver", "core-blue", "succeeded", "warning", "error", "feature", "info"
 * @param alt - Alt text for the image
 *
 * // Logo Avatar specific props
 * @param src - Required for type="logo". URL of the logo to display
 * @param alt - Alt text for the logo
 *
 * // Icon Avatar specific props
 * @param icon - Required for type="icon". Icon component to display
 * @param alt - Optional alt text for icon
 *
 * // Initial Avatar specific props
 * @param alt - Required for type="initial". Text to derive initial from
 * @param color - Color token for the initial avatar
 *
 * @returns {JSX.Element} Rendered avatar component based on type
 *
 * @throws {never} When an invalid type is provided (caught by TypeScript)
 *
 * @see {@link AvatarColor} for available color options
 * @see {@link AvatarStatus} for available status options
 * @see {@link AVATAR_COLOR_MAP} for color token mappings
 */

const Avatar = forwardRef<HTMLDivElement, AvatarProps>((props, ref) => {
  // Common props for all styled components
  const commonProps = {
    shape: "circle" as const,
    size: props.size ?? 24,
    $status: props.status,
    $stroke: props.stroke,
    $size: props.size,
    $hoverable: props.hoverable,
    $isGrayInitial: props.isGrayInitial,
    $alt: props.alt,
    ref,
  };

  // Type guards for each avatar type
  const isImageAvatar = (props: AvatarProps): props is AvatarImageProps => {
    return props.type === "image";
  };

  const isLogoAvatar = (props: AvatarProps): props is AvatarLogoProps => {
    return props.type === "logo";
  };

  const isIconAvatar = (props: AvatarProps): props is AvatarIconProps => {
    return props.type === "icon";
  };

  const isInitialAvatar = (props: AvatarProps): props is AvatarInitialProps => {
    return props.type === "initial";
  };

  // Render methods with proper type checking
  if (isImageAvatar(props)) {
    return (
      <StyledAvatar
        {...commonProps}
        src={props.src}
        alt={props.alt}
        $color={isValidAvatarColor(props.color) ? props.color : "core-silver"}
      />
    );
  }

  if (isLogoAvatar(props)) {
    return (
      <StyledLogoAvatar {...commonProps} src={props.src} alt={props.alt} />
    );
  }

  if (isIconAvatar(props)) {
    return (
      <StyledIconAvatar {...commonProps}>
        <Icon
          name={props.icon.name}
          color={props.icon?.color ?? COLOR_TOKENS.core.silver.a12}
        />
      </StyledIconAvatar>
    );
  }

  if (isInitialAvatar(props)) {
    return (
      <FallbackStyledAvatar {...commonProps}>
        {props.alt?.charAt(0) || "?"}
      </FallbackStyledAvatar>
    );
  }

  // Exhaustive check
  const _exhaustiveCheck: never = props;
  throw new Error("Unexpected avatar type");
});

Avatar.displayName = "Avatar";

export default Avatar;
